import { lazy } from 'react'
import { Route, Routes, Navigate } from 'react-router-dom'

import RequireAuth from '@/components/auth/RequireAuth'
import RequireReportAccess from '@/components/auth/RequireReportAccess'
import AdminIndexRedirect from '@/components/auth/AdminIndexRedirect'

import EmployeeHome from '@/routes/index'
import VacationPage from '@/routes/vacation'
import Login from '@/routes/login'
import ChangePassword from '@/routes/change-password'
import ForgotPassword from '@/routes/forgot-password'
import ResetPassword from '@/routes/reset-password'
import AcceptInvitation from '@/routes/accept-invitation'
import AdminLayout from '@/routes/admin/layout'
import AdminCalendar from '@/routes/admin/calendar'
import AdminDay from '@/routes/admin/day'
import UsersList from '@/routes/admin/users-list'
import UserCreate from '@/routes/admin/user-create'
import UserEdit from '@/routes/admin/user-edit'
import SettingsLayout from '@/routes/admin/settings-layout'
import PositionsSettings from '@/routes/admin/settings/positions'
import NotificationsSettings from '@/routes/admin/settings/notifications'
import ReportTeam from '@/routes/admin/report-team'
import ReportUser from '@/routes/admin/report-user'
import AdminVacationPage from '@/routes/admin/vacation'

/**
 * Jedyna ciężka zależność frontendu to maplibre-gl (~800 kB w bundlu).
 * Potrzebują jej dwa miejsca: mapa sesji w widoku dnia (już ładowana leniwie)
 * i ustawienia geofencingu. Dopóki ten drugi import był statyczny, maplibre
 * i tak lądowało w głównym bundlu, więc leniwy import mapy sesji nic nie dawał
 * — Rollup rozwiązywał go do modułu, który już tam siedział.
 *
 * Po odcięciu tego importu bibliotekę pobiera wyłącznie ten, kto faktycznie
 * otwiera mapę. Pracownik kliniki — czyli większość ruchu, w dodatku z telefonu
 * — nie zobaczy jej nigdy.
 */
const GeofencingSettings = lazy(
  () => import('@/routes/admin/settings/geofencing'),
)

export default function App() {
  return (
    <Routes>
      {/* Trasy publiczne */}
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/accept-invitation" element={<AcceptInvitation />} />

      {/* Trasy chronione — wymagają zalogowania */}
      <Route element={<RequireAuth />}>
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="/" element={<EmployeeHome />} />
        <Route path="/vacation" element={<VacationPage />} />
      </Route>

      {/* Trasy administracyjne — ADMIN + Manager/Księgowa (canManagePersonnel).
          Per-operation ADMIN guards na backendzie blokują wybrane akcje
          (tworzenie ADMIN, promocja do ADMIN, edycja admina). */}
      <Route element={<RequireReportAccess />}>
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminIndexRedirect />} />
          <Route path="report" element={<ReportTeam />} />
          <Route path="report/:userId" element={<ReportUser />} />
          <Route path="vacation" element={<AdminVacationPage />} />
          <Route path="calendar" element={<AdminCalendar />} />
          <Route path="day/:date" element={<AdminDay />} />
          <Route path="users" element={<UsersList />} />
          <Route path="users/new" element={<UserCreate />} />
          <Route path="users/:id" element={<UserEdit />} />
          <Route path="settings" element={<SettingsLayout />}>
            <Route
              index
              element={<Navigate to="/admin/settings/geofencing" replace />}
            />
            <Route path="geofencing" element={<GeofencingSettings />} />
            <Route path="positions" element={<PositionsSettings />} />
            <Route path="notifications" element={<NotificationsSettings />} />
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
